import {
  keepPreviousData,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { useEffect } from 'react';
import { PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants';

import { queryKeysUser } from '../queryKeys';

import { fetchUsersAdmin } from './fetchUsersAdmin';
import { type UsersAdminQueryParams, type UsersAdminResponse } from './types';
import { zodUsersAdminQueryParamsSchema } from './validations';

/**
 * Generates the query options for the users Admin query so they can be shared between the useQuery hook and the prefetching.
 */
export const getUsersAdminQueryOptions = ({
  queryParameters,
  options = {},
}: {
  queryParameters: Partial<UsersAdminQueryParams>;
  options?: Omit<UseQueryOptions<UsersAdminResponse>, 'queryKey' | 'queryFn'>;
}) => {
  const validatedSearchParams =
    zodUsersAdminQueryParamsSchema.parse(queryParameters);

  return {
    queryFn: async () => fetchUsersAdmin(validatedSearchParams),
    queryKey: queryKeysUser.getAllAdmin(validatedSearchParams),
    staleTime: ms('60s'), // prevent hitting the rate limit
    ...options,
  };
};

/**
 * React Query based custom hook for getting the data for all users with given query parameters an ADMIN has access to.
 * Does not limit the results by the user's permissions as the GET_ALL endpoint does. Requires ADMIN access.
 * Handles pagination and prefetches the next page of users for better performance.
 *
 * @param queryParameters - The query parameters for fetching users.
 * @param options - Additional options for the useQuery hook.
 * @returns A list of users.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/get_users_vadmin_users_get
 */
export const useQueryUsersAdmin = (
  queryParameters: Partial<UsersAdminQueryParams>,
  options?: Parameters<typeof useQuery>[0],
) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.prefetchQuery(
      getUsersAdminQueryOptions({
        options,
        queryParameters: {
          ...queryParameters,
          offset:
            (queryParameters.offset ?? 0) +
            (queryParameters.limit ?? PAGINATION_PAGE_SIZE_DEFAULT),
        },
      }),
    );
  }, [queryParameters, options, queryClient]);

  return useQuery<UsersAdminResponse>({
    ...getUsersAdminQueryOptions({ options, queryParameters }),
    placeholderData: keepPreviousData,
  });
};
